import { Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { PlatformService } from '@examdojo/platform';
import { UserRole } from '@examdojo/models/user';
import { IonContent, IonFooter, IonHeader, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { IconComponent } from '@examdojo/core/icon';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { ToastService } from '@examdojo/core/toast';
import { EMPTY, finalize, tap } from 'rxjs';
import { AdminUserDetailRoleSelectorComponent } from './detail-view/admin-user-detail-role-selector.component';
import { UserPromoteHttpService } from './user-promote-http.service';

@Component({
  selector: 'exam-invite-users-dialog',
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar class="toolbar-with-closing-button !px-2">
        <div slot="start">
          <dojo-icon-button (click)="closeDialog()" [icon]="'times'"></dojo-icon-button>
        </div>
        <!-- <div slot="end" class="w-8"></div> -->
        <ion-title class="common-ion-title">Invite Users</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="flex flex-col gap-2 p-4">
        <div class="flex items-end gap-4">
          <exam-admin-user-detail-role-selector
            [roleOptions]="['CONTRIBUTOR']"
            [formCtrl]="roleCtrl"
            [label]="'Role'"
            class="w-fit"
            [size]="'s'"
          />

          <div class="rounded-lg border border-sky-200 bg-sky-50 p-2">
            <div class="flex items-center gap-2">
              <dojo-icon [icon]="'info-circle'" [size]="16" class="mt-0.5 text-sky-600" />
              <div class="flex-1">
                <p class="m-0 text-sm text-sky-800">Invited users can have CONTRIBUTOR role only.</p>
              </div>
            </div>
          </div>
        </div>
        <div class="custom-mat-form-wrapper mt-4 grow">
          <div class="mat-form-label">
            <span>Email Addresses</span>
          </div>
          <mat-form-field class="size-s w-full" appearance="fill">
            <mat-chip-grid #chipGrid aria-label="Enter email addresses" [formControl]="emailsCtrl">
              @for (email of emails(); track email) {
                <mat-chip-row (removed)="removeEmail(email)">
                  <div class="flex items-center gap-2">
                    {{ email }}
                    <dojo-icon
                      [icon]="'times'"
                      (click)="removeEmail(email)"
                      [attr.aria-label]="'remove ' + email"
                    ></dojo-icon>
                  </div>
                </mat-chip-row>
              }
            </mat-chip-grid>
            <input
              placeholder="Add email address..."
              [matChipInputFor]="chipGrid"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              (matChipInputTokenEnd)="addEmail($event)"
            />
          </mat-form-field>
        </div>
      </div>
    </ion-content>
    <ion-footer class="border-t border-neutral-200 shadow-none">
      <div class="flex w-full justify-end gap-4 p-4">
        <dojo-button [size]="'s'" (click)="discard()" class="block" [fill]="'outline'">Cancel</dojo-button>
        <dojo-button
          [size]="'s'"
          (click)="confirm()"
          class="block"
          [pending]="isInviting()"
          [disabled]="emails().length === 0"
        >
          Invite
        </dojo-button>
      </div>
    </ion-footer>
  `,
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    IconButtonComponent,
    IonTitle,
    IconComponent,
    IonToolbar,
    IonHeader,
    MatChipsModule,
    MatIconModule,
    MatFormFieldModule,
    AdminUserDetailRoleSelectorComponent,
    IonFooter,
    IonContent,
  ],
})
export class InviteUsersDialogComponent {
  constructor(
    private readonly modalController: ModalController,
    private readonly platformService: PlatformService,
    private readonly userPromoteHttpService: UserPromoteHttpService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef,
  ) {}

  readonly emails = signal<string[]>([]);
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly isInviting = signal<boolean>(false);
  readonly emailsCtrl = new FormControl<string[]>([], { nonNullable: true });
  readonly roleCtrl = new FormControl<UserRole>('CONTRIBUTOR', { nonNullable: true });

  readonly state = connectState({
    isMobile: this.platformService.isSmall$,
  });

  addEmail(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value && this.isValidEmail(value)) {
      this.emails.update((emails) => [...emails, value]);
    }

    event.chipInput?.clear();
  }

  removeEmail(email: string) {
    this.emails.update((emails) => {
      const index = emails.indexOf(email);
      if (index < 0) {
        return emails;
      }

      emails.splice(index, 1);
      return [...emails];
    });
  }

  discard() {
    this.modalController.dismiss(null);
  }

  confirm() {
    if (this.emails().length === 0) {
      return;
    }

    this.isInviting.set(true);

    this.userPromoteHttpService
      .promoteUsers(this.roleCtrl.value, this.emails())
      .pipe(
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        tap((response) => {
          if (response?.result?.status === 'success') {
            this.toastService.success('Users invited successfully');
            this.modalController.dismiss(true);
          } else {
            const errorMessage = this.getErrorMessage(response?.result?.error_code, response?.result?.emails);
            this.toastService.error(errorMessage);
          }
        }),
        finalize(() => this.isInviting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  closeDialog() {
    this.modalController.dismiss(null);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private getErrorMessage(errorCode: 'invalid_email' | 'already_on_platform', emails: string[] = []): string {
    const emailList = emails.join(', ');

    switch (errorCode) {
      case 'invalid_email':
        return `Invalid email address${emails.length > 1 ? 'es' : ''}: ${emailList}`;
      case 'already_on_platform':
        return `User${emails.length > 1 ? 's' : ''} already on platform: ${emailList}`;
      default:
        return 'An error occurred while inviting users';
    }
  }
}
